import React, { useEffect, useState } from "react";
import { LuEye } from "react-icons/lu";
import { getImpersonation } from "../../services/auth/impersonation-service";
import { endImpersonation } from "../../services/support/support-service";

const minutesLeft = expiresAt => Math.max(0, Math.ceil((expiresAt * 1000 - Date.now()) / 60000));

/**
 * Shown on every console page while a support impersonation session is active: a red frame
 * around the viewport plus a pill in the header. Ends the session when it expires.
 */
export default function ImpersonationBanner() {
  const [session] = useState(() => getImpersonation());
  const [left, setLeft] = useState(() => (session ? minutesLeft(session.expiresAt) : 0));
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    if (!session) return undefined;
    const timer = setInterval(() => {
      const remaining = minutesLeft(session.expiresAt);
      setLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        endImpersonation();
      }
    }, 15000);
    return () => clearInterval(timer);
  }, [session]);

  if (!session) return null;

  const handleEnd = async () => {
    setEnding(true);
    await endImpersonation();
  };

  return (
    <>
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[70] ring-4 ring-inset ring-red-500" />
      <div
        role="status"
        aria-label="Support impersonation session"
        className="fixed left-1/2 top-2.5 z-[71] flex -translate-x-1/2 items-center gap-3 rounded-full bg-red-600 py-1.5 pl-3 pr-1.5 text-[13px] font-medium text-fixed-white shadow-lg"
      >
        <LuEye className="h-4 w-4 flex-none" aria-hidden="true" />
        <span className="truncate">
          Viewing <strong>{session.targetAccountName || session.targetAccountId}</strong> as support
          <span className="mx-1.5 opacity-70">·</span>read-only
          <span className="mx-1.5 opacity-70">·</span>{left} min left
        </span>
        <button
          type="button"
          onClick={handleEnd}
          disabled={ending}
          className="flex-none rounded-full bg-white px-3 py-1 text-[12px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
        >
          {ending ? 'Ending…' : 'End session'}
        </button>
      </div>
    </>
  );
}
